import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { Label, Paragraph, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { Heading } from '@/components/heading';
import { Input } from '@/components/input';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { TextLink } from '@/components/text-link';
import { Wordmark } from '@/components/wordmark';
import { useClearPasswordRecovery } from '@/hooks/auth-context';
import { auth } from '@/lib/auth';
import { MIN_PASSWORD_LENGTH } from '@/lib/password';

// Reachable only after clicking a password-reset email link, which
// establishes a real (recovery) session -- see use-auth-state.ts's
// deriveAuthState for why that needs separate tracking from a normal
// sign-in. Rendered by the root layout directly, overriding the usual
// auth-state routing, so a user who just wants a new password isn't
// dropped into whatever screen their normal auth state would otherwise
// show first.
export function ResetPassword() {
  const clearPasswordRecovery = useClearPasswordRecovery();
  const [password, setPassword] = useState('');

  // Tracked so it can be cleared on unmount (issue #126 AC3) -- without
  // this, navigating away within the 1200ms window still fired
  // clearPasswordRecovery against a screen the user had already left.
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current);
    };
  }, []);

  const submit = useMutation({
    mutationFn: () => auth.updatePassword(password),
    // A brief pause before leaving this screen so "Password updated"
    // is actually readable -- clearing immediately swapped straight to
    // whatever the normal auth gate shows next, with no confirmation
    // ever visible on screen.
    onSuccess: () => {
      clearTimeoutRef.current = setTimeout(clearPasswordRecovery, 1200);
    },
  });

  // The recovery session Supabase establishes on a reset-link click is
  // a full, real session -- clearing the recovery flag alone would
  // just drop a user who backs out here straight into the app. Signing
  // out first means "cancel" actually returns to a signed-out state.
  const cancel = useMutation({
    mutationFn: () => auth.signOut(),
    onSuccess: clearPasswordRecovery,
  });

  return (
    <ScreenContainer>
      <YStack pt="$8" pb="$4">
        <Wordmark />
      </YStack>
      <YStack flex={1} alignItems="center" justifyContent="center" gap="$4">
        <Heading textAlign="center">Set a new password</Heading>
        <Paragraph color="$colorSecondary" textAlign="center">
          Choose a new password for your account.
        </Paragraph>

        {submit.isSuccess ? (
          <Paragraph color="$colorSecondary" textAlign="center">
            Password updated. Signing you in...
          </Paragraph>
        ) : (
          <>
            <YStack width="100%" gap="$2">
              <Label htmlFor="new-password" color="$colorSecondary">
                New password
              </Label>
              <Input
                id="new-password"
                value={password}
                onChangeText={(value) => {
                  setPassword(value);
                  if (submit.isError) submit.reset();
                }}
                secureTextEntry
                textContentType="newPassword"
                borderColor={submit.isError ? '$destructive' : undefined}
              />
              <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                At least {MIN_PASSWORD_LENGTH} characters.
              </Text>
            </YStack>

            <PrimaryButton
              disabled={password.length < MIN_PASSWORD_LENGTH}
              loading={submit.isPending}
              onPress={() => {
                if (!submit.isPending) submit.mutate();
              }}
            >
              {submit.isPending ? <ActivityIndicator color="white" /> : 'Save new password'}
            </PrimaryButton>

            {submit.isError && (
              <Paragraph color="$destructive" textAlign="center">
                {submit.error.message}
              </Paragraph>
            )}

            <TextLink
              textAlign="center"
              onPress={() => {
                if (!cancel.isPending) cancel.mutate();
              }}
            >
              {cancel.isPending ? 'Signing out...' : 'Cancel and sign in instead'}
            </TextLink>
          </>
        )}
      </YStack>
    </ScreenContainer>
  );
}
